import {connect} from 'react-redux';
import {Component, Fragment} from 'react';
import injectSheet from 'react-jss';
import {arrayToSentence} from '@deliveriesondemand/dod-utils';

import {mainApplicantNameSelector} from '-/selectors';
import NavigationService from '-/services/NavigationService';
import FunnelService from '-/services/FunnelService';
import UIService from '-/services/UIService';
import validationStyles from '-/styles/sections/validation';
import {center, rem, size, smallScreen} from '-/styles/utils';
import {get} from '-/util/lodash';

const styles = theme => {
    const {description, icon, headerImg, text} = validationStyles(theme);
    return {
        description: {
            ...description,
            paddingTop: 10,
            display: 'block'
        },
        icon,
        headerImg,
        text: {
            ...text,
            margin: 0
        },
        options: {
            padding: `0 5% ${rem(80)}`
        },
        or: {
            margin: 15,
            fontSize: rem(20),
            fontWeight: 'bold',
            lineHeight: rem(27),
            textTransform: 'capitalize',
            textAlign: 'center',
            opacity: .9,
            color: theme.colors.primary
        },
        box: {
            display: 'block',
            marginBottom: rem(8),
            border: '4px solid #414042',
            borderRadius: 100,
            textDecoration: 'none',
            color: '#757575',
            background: 'white',
            cursor: 'pointer',
            '&:link, &:visited': {
                color: '#757575'
            },
            '&:last-child': {
                marginBottom: 0
            }
        },
        title: {
            display: 'block',
            marginBottom: rem(12),
            ...smallScreen({
                marginBottom: rem(6)
            })
        },
        optionIcon: {
            ...size('20%'),
            display: 'inline-block',
            padding: '5%',

            '& i': {
                fontSize: '4em !important',
                verticalAlign: 'middle',
                color: theme.colors.primary,
                textAlign: 'center',
                width: '100%'
            },

            '@media all and (max-width: 726px)': {
                '& i': {
                    fontSize: '3em !important'
                }
            }
        },
        check: {
            ...size('30px'),
            position: 'absolute',
            bottom: '.5rem',
            right: rem(-10),
            borderRadius: '50%',
            backgroundColor: theme.colors.primary,
            '&::before, &:before': {
                ...center('both'),
                content: '"\\f00c"',
                display: 'block',
                fontFamily: 'FontAwesome',
                fontSize: rem(18),
                fontWeight: 'normal',
                lineHeight: rem(24),
                color: 'white'
            }
        },
        details: {
            display: 'inline-block',
            verticalAlign: 'middle',
            marginLeft: rem(5),
            width: 'calc(80% - 5px)'
        },
        h5: {
            margin: 0,
            fontSize: rem(14),
            fontWeight: 'normal',
            textAlign: 'center',
            lineHeight: rem(20),
            color: theme.colors.strong
        },
        titleH5: {
            margin: 0,
            fontSize: rem(20),
            fontWeight: 'bold'
        },
        p: {
            margin: 0,
            fontSize: rem(14)
        },
        giftCard: {
            width: '60%',
            textAlign: 'center',
            margin: '20px auto',
            fontWeight: 500,

            '& p': {
                lineHeight: '1.5em',
                fontSize: '1.25em'
            }
        },
        warning: {
            margin: '15px 39px',
            textAlign: 'center',
            borderWidth: 2,
            borderStyle: 'solid',
            borderColor: theme.colors.primary,
            backgroundColor: '#fff',
            padding: '10px 5px',
            fontWeight: 'bold'
        }
    };
};

class ValidationInfo extends Component {
    componentDidMount() {
        const {componentDidMount} = this.props;

        if (componentDidMount) {
            componentDidMount();
        }
    }

    options() {
        const {
            onDobClick,
            onSSNClick,
            onAccountNumberClick,
            onAddressClick,
            mainApplicantName,
            coAppUnlocked
        } = this.props;
        const nameString = coAppUnlocked ? `${mainApplicantName}'s` : 'your';

        return {
            dob: {
                faTag: 'fa fa-birthday-cake',
                imgAlt: 'Birthday Cake',
                title: 'Date of Birth',
                shortTitle: 'DOB',
                subtitle: `Enter in ${nameString} date of birth.`,
                onClick: onDobClick
            },
            ssn: {
                faTag: 'fa fa-shield',
                imgAlt: 'Shield',
                title: 'Social Security Number',
                shortTitle: 'SSN',
                subtitle: `Enter in the last 4 digits of ${nameString} SSN.`,
                onClick: onSSNClick
            },
            account: {
                faTag: 'fa fa-id-card',
                imgAlt: 'ID Card',
                title: 'Your Account Number',
                subtitle: `Enter in ${nameString} credit union number.`,
                onClick: onAccountNumberClick
            },
            address: {
                faTag: 'fa fa-home',
                imgAlt: 'Address',
                title: 'Your House Number and Zip',
                subtitle: `Enter in ${nameString} house number and zip.`,
                onClick: onAddressClick
            }
        };
    }

    renderOptions() {
        const {validationOptions, classes = {}} = this.props;
        const options = this.options();

        return validationOptions.filter((option) => {
            return options.hasOwnProperty(option);
        }).map((option, i) => {
            option = options[option];

            return (
                <div key={i}>
                    {i > 0 ? (
                        <div className={classes.or}>Or</div>
                    ) : null}
                    <a onClick={option.onClick} title={option.title} className={classes.box}>
                        <span className={classes.optionIcon}>
                            <i alt={option.imgAlt} className={option.faTag} />
                        </span>
                        <span className={classes.details}>
                            <span className={classes.title}>
                                <h5 className={classes.titleH5}>{option.title}</h5>
                            </span>
                            <span className={classes.description}>
                                <p className={classes.p}>{option.subtitle}</p>
                            </span>
                        </span>
                    </a>
                </div>
            );
        });
    }

    renderCoAppWarning() {
        const {validationOptions, mainApplicantName, coAppUnlocked, classes = {}} = this.props;
        if (!coAppUnlocked) {
            return;
        }

        const options = this.options();
        const optionsString = validationOptions.filter((option) => {
            return options.hasOwnProperty(option);
        }).map(option => {
            option = options[option] || {};
            const {title, shortTitle} = option;
            let text = shortTitle || title;
            text = text.replace('Your ', '');

            return text;
        });

        return (
            <p className={classes.warning}>
                Use {mainApplicantName}&apos;s {arrayToSentence(optionsString, {lastSeparator: ' or '})}
            </p>
        );
    }

    render() {
        const {classes = {}, fromWorkflow} = this.props;

        return (
            <div>
                <section className={classes.description}>
                    {!fromWorkflow && (
                        <Fragment>
                            <div className={classes.icon}>
                                <i className="fa fa-id-card" alt="ID Card" />
                                <span className={classes.check} />
                            </div>
                            <div className={classes.text}>
                                <h3>Choose One Option</h3>
                            </div>
                        </Fragment>
                    )}
                    <div className={classes.text}>
                        {this.renderCoAppWarning()}
                        {fromWorkflow && (
                            <div className={classes.giftCard}>
                                <h1>Yay! You made it!</h1>
                                <p>
                                    We just need to make sure you are the right person.
                                    Please choose an option to secure your gift card.
                                </p>
                            </div>
                        )}
                    </div>
                </section>
                <section className={classes.options}>
                    {this.renderOptions()}
                </section>
            </div>
        );
    }
}
const mapStateToProps = state => {
    const mainApplicantName = mainApplicantNameSelector(state);
    const applicantType = get(state, 'stips.applicantType');
    const isCoApp = applicantType === 'coApp';
    const {validationOptions} = state.config;

    return {
        fromWorkflow: UIService.getFromWorkflow(),
        isCoApp,
        validationOptions,
        mainApplicantName,
        coAppUnlocked: state.navigation.coAppUnlocked
    };
};

const authPageMap = {
    account: 'verifyAccount',
    dob: 'verifyDob',
    ssn: 'verifySSN',
    address: 'verifyAddress'
};

const mapDispatchToProps = (dispatch) => {
    return {
        componentDidMount: () => {
            dispatch((dispatch, getState) => {
                const {validationOptions} = getState().config;

                FunnelService.authMethodShown(validationOptions);

                // A single option skips this screen entirely, so record the method as
                // selected rather than leaving it looking like the member chose nothing.
                if (validationOptions.length === 1) {
                    FunnelService.authMethodSelected(validationOptions[0]);
                    NavigationService.next(authPageMap[validationOptions[0]]);
                }
            });
        },
        onDobClick: () => {
            FunnelService.authMethodSelected('dob');
            NavigationService.next('verifyDob');
        },
        onSSNClick: () => {
            FunnelService.authMethodSelected('ssn');
            NavigationService.next('verifySSN');
        },
        onAccountNumberClick: () => {
            FunnelService.authMethodSelected('account');
            NavigationService.next('verifyAccount');
        },
        onAddressClick: () => {
            FunnelService.authMethodSelected('address');
            NavigationService.next('verifyAddress');
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(injectSheet(styles)(ValidationInfo));
